/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import css from '@emotion/css/macro'
import { Tooltip } from 'antd'
import {
  EmailIcon,
  FacebookIcon,
  WhatsappIcon,
  XIcon,
  LinkedinIcon,
  RedditIcon,
  PinterestIcon,
  ThreadsIcon,
  BlueskyIcon,
} from 'react-share'

import colors from '@sportninja/common/constants/appColors'

interface ShareButtonsProps {
  shareUrl: string
  shareTitle: string
  shareDescription?: string
  entityType: string
}

/** Builds a URL with UTM tracking parameters */
function buildUtmUrl(
  baseUrl: string,
  platform: string,
  entityType: string
): string {
  const separator = baseUrl.includes('?') ? '&' : '?'
  return `${baseUrl}${separator}utm_source=${platform}&utm_medium=social&utm_campaign=share&utm_content=${entityType}`
}

const ICON_SIZE = 44

const buttonStyles = css`
  width: ${ICON_SIZE}px;
  height: ${ICON_SIZE}px;
  border: none;
  padding: 0;
  background: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: transform 0.15s ease;

  &:hover {
    transform: scale(1.1);
  }
`

type SharePlatform = {
  key: string
  label: string
  getUrl: () => string
  icon: React.ReactNode
}

/**
 * Horizontally scrollable row of social-share icon buttons.
 * Opens each platform's share intent in a new tab with UTM tracking.
 *
 * @param shareUrl - Base URL to share
 * @param shareTitle - Text used as the share title/subject
 * @param shareDescription - Optional description for platforms that support it
 * @param entityType - Entity kind used for UTM content param
 */
function ShareButtons({
  shareUrl,
  shareTitle,
  shareDescription = '',
  entityType,
}: ShareButtonsProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [showArrow, setShowArrow] = useState(false)

  const handleOverflow = useCallback(() => {
    if (!scrollRef.current) return
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current
    setShowArrow(
      scrollWidth > clientWidth && scrollLeft + clientWidth < scrollWidth - 8
    )
  }, [])

  useEffect(() => {
    handleOverflow()
    window.addEventListener('resize', handleOverflow)
    return () => window.removeEventListener('resize', handleOverflow)
  }, [handleOverflow])

  const urls = useMemo(
    () => ({
      email: buildUtmUrl(shareUrl, 'email', entityType),
      facebook: buildUtmUrl(shareUrl, 'facebook', entityType),
      whatsapp: buildUtmUrl(shareUrl, 'whatsapp', entityType),
      x: buildUtmUrl(shareUrl, 'x', entityType),
      linkedin: buildUtmUrl(shareUrl, 'linkedin', entityType),
      reddit: buildUtmUrl(shareUrl, 'reddit', entityType),
      pinterest: buildUtmUrl(shareUrl, 'pinterest', entityType),
      threads: buildUtmUrl(shareUrl, 'threads', entityType),
      bluesky: buildUtmUrl(shareUrl, 'bluesky', entityType),
    }),
    [shareUrl, entityType]
  )

  const platforms: SharePlatform[] = useMemo(
    () => [
      {
        key: 'email',
        label: 'Email',
        getUrl: () =>
          `mailto:?subject=${encodeURIComponent(
            shareTitle
          )}&body=${encodeURIComponent(
            `${shareDescription || `Check out this ${entityType}!`}\n\n${
              urls.email
            }`
          )}`,
        icon: <EmailIcon size={ICON_SIZE} round />,
      },
      {
        key: 'facebook',
        label: 'Facebook',
        getUrl: () =>
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            urls.facebook
          )}`,
        icon: <FacebookIcon size={ICON_SIZE} round />,
      },
      {
        key: 'whatsapp',
        label: 'WhatsApp',
        getUrl: () =>
          `https://wa.me/?text=${encodeURIComponent(
            `${shareTitle} - ${urls.whatsapp}`
          )}`,
        icon: <WhatsappIcon size={ICON_SIZE} round />,
      },
      {
        key: 'x',
        label: 'X',
        getUrl: () =>
          `https://x.com/intent/tweet?text=${encodeURIComponent(
            shareTitle
          )}&url=${encodeURIComponent(urls.x)}`,
        icon: <XIcon size={ICON_SIZE} round />,
      },
      {
        key: 'linkedin',
        label: 'LinkedIn',
        getUrl: () =>
          `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(
            `${shareTitle}\n${urls.linkedin}`
          )}`,
        icon: <LinkedinIcon size={ICON_SIZE} round />,
      },
      {
        key: 'reddit',
        label: 'Reddit',
        getUrl: () =>
          `https://www.reddit.com/submit?url=${encodeURIComponent(
            urls.reddit
          )}&title=${encodeURIComponent(shareTitle)}`,
        icon: <RedditIcon size={ICON_SIZE} round />,
      },
      {
        key: 'pinterest',
        label: 'Pinterest',
        getUrl: () =>
          `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(
            urls.pinterest
          )}&description=${encodeURIComponent(shareTitle)}`,
        icon: <PinterestIcon size={ICON_SIZE} round />,
      },
      {
        key: 'threads',
        label: 'Threads',
        getUrl: () =>
          `https://www.threads.net/intent/post?text=${encodeURIComponent(
            `${shareTitle} ${urls.threads}`
          )}`,
        icon: <ThreadsIcon size={ICON_SIZE} round />,
      },
      {
        key: 'bluesky',
        label: 'Bluesky',
        getUrl: () =>
          `https://bsky.app/intent/compose?text=${encodeURIComponent(
            `${shareTitle} ${urls.bluesky}`
          )}`,
        icon: <BlueskyIcon size={ICON_SIZE} round />,
      },
    ],
    [shareTitle, shareDescription, entityType, urls]
  )

  const handleShare = useCallback((platform: SharePlatform) => {
    const url = platform.getUrl()
    window.open(url, '_blank', 'noopener,noreferrer')
  }, [])

  const handleArrowClick = useCallback(() => {
    if (!scrollRef.current) {
      return
    }
    scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' })
  }, [])

  return (
    <div
      css={css`
        position: relative;
      `}
    >
      <div
        ref={scrollRef}
        onScroll={handleOverflow}
        css={css`
          display: flex;
          gap: 16px;
          align-items: center;
          overflow-x: auto;
          overflow-y: hidden;

          /* Hide scrollbar */
          -ms-overflow-style: none;
          scrollbar-width: none;
          &::-webkit-scrollbar {
            display: none;
          }
        `}
      >
        {platforms.map((platform) => (
          <Tooltip key={platform.key} title={platform.label}>
            <button
              type='button'
              css={buttonStyles}
              aria-label={platform.label}
              onClick={() => handleShare(platform)}
            >
              {platform.icon}
            </button>
          </Tooltip>
        ))}
      </div>

      {/* Scroll arrow indicator */}
      {showArrow && (
        <button
          type='button'
          aria-label='Scroll right'
          onClick={handleArrowClick}
          css={css`
            position: absolute;
            right: -8px;
            top: 50%;
            transform: translateY(-50%);
            width: 40px;
            height: 40px;
            border: none;
            padding: 0;
            border-radius: 50%;
            background-color: ${colors.SECONDARY_100};
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            z-index: 1;
            transition: background-color 0.15s ease;

            &:hover {
              background-color: ${colors.SECONDARY_200};
            }
          `}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            height='24px'
            viewBox='0 -960 960 960'
            width='24px'
            fill={colors.WHITE}
          >
            <path d='M504-480 320-664l56-56 240 240-240 240-56-56 184-184Z' />
          </svg>
        </button>
      )}
    </div>
  )
}

export default ShareButtons
